import { useEffect, useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineMenuAlt4, HiOutlineX } from 'react-icons/hi';
import { GiLotus } from 'react-icons/gi';
import { properties } from '../../data/properties';
import './Navbar.css';

// Short location label pulled from each property for the nav sub-text
const propertyLocations = {
  'salkara-inn-main': 'Palakkad',
  'salkara-inn-madukkarai': 'Madukkarai',
  'salkara-nest': 'Chettipalayam Pirivu',
  'salkara-mini-walayar': 'Walayar',
};

// Map property IDs to display names with location
const propertyDisplayNames = {
  'salkara-inn-main': 'Salkara Inn (Kanjikode)',
  'salkara-mini-walayar': 'Salkara Mini (Walayar)',
  'salkara-inn-madukkarai': 'Salkara Inn (Madukkarai)',
  'salkara-nest': 'Salkara Nest (Chettipalayam Pirivu)',
};

const leftProperties = properties.filter((p) =>
  ['salkara-inn-main','salkara-mini-walayar'].includes(p.id)
);
const rightProperties = properties.filter((p) =>
  ['salkara-nest', 'salkara-inn-madukkarai' ].includes(p.id)
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Helper function to render property link with location
  const renderPropertyLink = (property, isMain = false) => {
    const displayName = propertyDisplayNames[property.id] || property.name;
    // Extract location from display name (text in brackets)
    const locationMatch = displayName.match(/\((.*?)\)/);
    const location = locationMatch ? locationMatch[1] : propertyLocations[property.id] || '';
    const nameWithoutLocation = displayName.replace(/\s*\(.*?\)\s*/, '').trim();

    return (
      <NavLink
        key={property.id}
        to={`/properties/${property.id}`}
        className={({ isActive }) => 
          `navbar__hotel-link ${isActive ? 'navbar__hotel-link--active' : ''} ${isMain ? 'navbar__hotel-link--main' : ''}`
        }
      >
        <span className="navbar__hotel-name">
          {isMain && <GiLotus className="navbar__main-icon" />}
          {nameWithoutLocation}
          {isMain && <span className="navbar__main-badge">Main</span>}
        </span>
        <span className="navbar__hotel-sub">{location}</span>
      </NavLink>
    );
  };

  // Helper function for drawer links
  const renderDrawerLink = (property, index) => {
    const displayName = propertyDisplayNames[property.id] || property.name;
    const locationMatch = displayName.match(/\((.*?)\)/);
    const location = locationMatch ? locationMatch[1] : propertyLocations[property.id] || '';
    const nameWithoutLocation = displayName.replace(/\s*\(.*?\)\s*/, '').trim();
    const isMain = property.id === 'salkara-inn-main';

    return (
      <motion.div
        key={property.id}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 + index * 0.06, duration: 0.5 }}
      >
        <NavLink 
          to={`/properties/${property.id}`} 
          onClick={() => setOpen(false)}
          className={({ isActive }) => 
            `navbar__drawer-link ${isActive ? 'navbar__drawer-link--active' : ''} ${isMain ? 'navbar__drawer-link--main' : ''}`
          }
        >
          <span className="navbar__drawer-link-content">
            {isMain && <GiLotus className="navbar__drawer-main-icon" />}
            {nameWithoutLocation}
            {isMain && <span className="navbar__drawer-main-badge">★ Main</span>}
          </span>
          <span className="navbar__drawer-sub">{location}</span>
        </NavLink>
      </motion.div>
    );
  };

  return (
    <>
      <header className={`navbar ${scrolled ? 'navbar--solid' : ''}`}>
        <div className="navbar__inner container">
          <nav className="navbar__group navbar__group--left">
            {leftProperties.map((property) => {
              const isMain = property.id === 'salkara-inn-main';
              return renderPropertyLink(property, isMain);
            })}
          </nav>

          <Link to="/" className="navbar__brand" onClick={() => setOpen(false)}>
            <img src="/sakaralogo.png" alt="Salkara Group" />
          </Link>

          <nav className="navbar__group navbar__group--right">
            {rightProperties.map((property) => {
              return renderPropertyLink(property, false);
            })}
          </nav>

          <button
            className="navbar__toggle"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((prev) => !prev)}
          >
            {open ? <HiOutlineX /> : <HiOutlineMenuAlt4 />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="navbar__drawer"
            initial={{ clipPath: 'inset(0 0 100% 0)' }}
            animate={{ clipPath: 'inset(0 0 0% 0)' }}
            exit={{ clipPath: 'inset(0 0 100% 0)' }}
            transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
          >
            <nav className="navbar__drawer-links">
              {properties.map((property, i) => renderDrawerLink(property, i))}
            </nav>
            <Link to="/contact" className="btn btn-gold" onClick={() => setOpen(false)}>
              Book Now
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}